import React from 'react';
import {
  Box,
  Text,
  Heading,
  SimpleGrid,
  VStack,
  Divider,
} from '@chakra-ui/react';
import { useBinderCardColors } from '../../binder_report/hooks/useBinderCardColors';

export interface BillingField {
  field: string;
  value: string;
  section?: string;
}

export interface BillingData {
  billingInfo: BillingField[];
}

interface HomeBillingCardProps {
  sectionData: {
    data?: BillingData;
  };
}

const HomeBillingCard: React.FC<HomeBillingCardProps> = ({ sectionData }) => {
  const { data } = sectionData || {};
  const { billingInfo = [] } = data || {};
  const colors = useBinderCardColors();

  if (billingInfo.length === 0) {
    return (
      <Box p={4} borderRadius="md" bg={colors.emptyFieldBgColor}>
        <Text color="gray.500" fontStyle="italic" textAlign="center">
          No billing information available
        </Text>
      </Box>
    );
  }

  // Group fields by section
  const mainFields = billingInfo.filter((item) => !item.section);
  const eftFields = billingInfo.filter((item) => item.section === 'eft');
  const creditCardFields = billingInfo.filter(
    (item) => item.section === 'credit_card',
  );
  const downpaymentFields = billingInfo.filter(
    (item) => item.section === 'downpayment',
  );
  const downpaymentEftFields = billingInfo.filter(
    (item) => item.section === 'downpayment_eft',
  );
  const downpaymentCreditCardFields = billingInfo.filter(
    (item) => item.section === 'downpayment_credit_card',
  );

  const renderFields = (fields: BillingField[]) => (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
      {fields.map((item, index) => (
        <Box key={index}>
          <Text
            fontSize="xs"
            color={colors.descriptionColor}
            mb={1}
            fontWeight="medium"
          >
            {item.field}
          </Text>
          <Text fontSize="sm" color={colors.textColor} fontWeight="semibold">
            {item.value || 'N/A'}
          </Text>
        </Box>
      ))}
    </SimpleGrid>
  );

  return (
    <Box
      borderRadius="md"
      borderWidth="1px"
      borderColor={colors.borderColor}
      overflow="hidden"
    >
      {/* Header */}
      <Box
        p={4}
        bg={colors.emptyFieldBgColor}
        borderBottomWidth="1px"
        borderColor={colors.borderColor}
      >
        <Heading size="sm" color={colors.textColor}>
          Billing
        </Heading>
      </Box>

      {/* Billing Fields */}
      <VStack align="stretch" spacing={4} p={4}>
        {/* Main Payment Fields */}
        {mainFields.length > 0 && renderFields(mainFields)}

        {/* EFT Payment Fields */}
        {eftFields.length > 0 && (
          <>
            <Divider />
            {renderFields(eftFields)}
          </>
        )}

        {/* Credit Card Payment Fields */}
        {creditCardFields.length > 0 && (
          <>
            <Divider />
            {renderFields(creditCardFields)}
          </>
        )}

        {/* Down Payment Fields */}
        {downpaymentFields.length > 0 && (
          <>
            <Divider />
            {renderFields(downpaymentFields)}
          </>
        )}

        {/* Down Payment EFT Fields */}
        {downpaymentEftFields.length > 0 && renderFields(downpaymentEftFields)}

        {/* Down Payment Credit Card Fields */}
        {downpaymentCreditCardFields.length > 0 &&
          renderFields(downpaymentCreditCardFields)}
      </VStack>
    </Box>
  );
};

export default HomeBillingCard;
